import { useCallback, useEffect, useState } from 'react'
import { TextInput } from '@jana-technology/platform-ui'
import { Button } from '@jana-technology/platform-ui/button'

import {
  IAcceptanceCategory,
  RiskThresholdLevelCode
} from 'app/services/api/acceptanceCategories'

import { riskThresholdsTemplate } from './constants'
import { RiskThresholdType } from './types'

interface IThresholdEditorProps {
  riskType: RiskThresholdType
  riskThresholds: IAcceptanceCategory[] | undefined
  onRiskThresholdsChange: (updatedRiskThresholds: IAcceptanceCategory[]) => void
}

interface IEditThresholdRowProps {
  isRiskDensity: boolean
  thresholdRow: IAcceptanceCategory
  onThresholdRowChange: (updatedThreshold: IAcceptanceCategory) => void
}

const RiskThresholdLevelColorBlock = ({
  thresholdName,
  thresholdColor
}: {
  thresholdName: string
  thresholdColor: string
}) => {
  return (
    <div className='flex items-center'>
      <div
        className='flex-none mr-2 size-9 rounded-sm'
        style={{ backgroundColor: '#' + thresholdColor }}
      ></div>
      <div className='text-sm font-medium'>{thresholdName}</div>
    </div>
  )
}

const numberFormatter = new Intl.NumberFormat('en-US')

export const EditThresholdRow = ({
  isRiskDensity,
  thresholdRow,
  onThresholdRowChange
}: IEditThresholdRowProps) => {
  // Format number values for display (i.e. allow ',' separator)
  const formatValue = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return ''
    }

    return numberFormatter.format(value)
  }

  // Parse value from formatted number input (with commas)
  // Treat empty string as NaN
  const parseValue = (value: string) => {
    const raw = value.replaceAll(/[^\d]/g, '')

    if (raw === '') {
      return Number.NaN
    }

    return Number(raw)
  }

  // Extract only numbers from input
  const sanitizeInput = (value: string) => value.replaceAll(/[^\d,]/g, '')

  return (
    <div className='grid grid-cols-[8.25rem_1fr_1fr] gap-x-4 items-center my-4'>
      <RiskThresholdLevelColorBlock
        thresholdName={thresholdRow.name}
        thresholdColor={thresholdRow.colour}
      />
      <div className='flex items-center'>
        <TextInput
          className='input-no-stepper h-8'
          value={formatValue(
            isRiskDensity ? thresholdRow.minDensityValue : thresholdRow.minValue
          )}
          onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
            const sanitized = sanitizeInput(evt.target.value)
            const value = parseValue(sanitized)

            onThresholdRowChange({
              ...thresholdRow,
              ...(isRiskDensity
                ? { minDensityValue: value }
                : { minValue: value })
            })
          }}
          disabled={
            thresholdRow.code === RiskThresholdLevelCode.LowRisk &&
            (isRiskDensity
              ? thresholdRow.minDensityValue === 0
              : thresholdRow.minValue === 0)
          }
          inputMode='numeric'
        />
      </div>
      <div className='flex items-center'>
        {(() => {
          let isMax = false
          let value = 0

          if (isRiskDensity) {
            isMax = thresholdRow.maxDensityValue >= Number.MAX_VALUE
            value = thresholdRow.maxDensityValue
          } else {
            isMax = thresholdRow.maxValue >= Number.MAX_VALUE
            value = thresholdRow.maxValue
          }

          if (isMax) {
            return <TextInput className='h-8' value='MAX' disabled />
          } else {
            return (
              <TextInput
                className='input-no-stepper h-8'
                value={formatValue(value)}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                  const sanitized = sanitizeInput(evt.target.value)
                  const value = parseValue(sanitized)

                  onThresholdRowChange({
                    ...thresholdRow,
                    ...(isRiskDensity
                      ? { maxDensityValue: value }
                      : { maxValue: value })
                  })
                }}
                inputMode='numeric'
              />
            )
          }
        })()}
      </div>
    </div>
  )
}

export const ThresholdEditor = ({
  riskType,
  riskThresholds,
  onRiskThresholdsChange
}: IThresholdEditorProps) => {
  const [isDefaultThresholds, setIsDefaultThresholds] = useState(true)

  const isRiskDensity = riskType === RiskThresholdType.RiskDensity
  const riskUnitDisplay = isRiskDensity ? 'RU/mi/yr' : 'RU/yr'

  // enabled risk threshold count
  const enabledCount =
    (riskThresholds || []).filter((t) => t.enabled).length || 3

  const resetFields = useCallback(() => {
    onRiskThresholdsChange([...riskThresholdsTemplate[enabledCount]])
  }, [onRiskThresholdsChange, enabledCount])

  useEffect(() => {
    const template = riskThresholdsTemplate[enabledCount]

    let isDefault = true

    if (riskThresholds && template) {
      for (const threshold of riskThresholds) {
        const templateObj = template.find((t) => t.code === threshold.code)

        if (!templateObj) {
          console.error(
            `Template object not found for threshold: ${threshold.code}`
          )

          continue
        }

        if (
          threshold.minValue !== templateObj.minValue ||
          threshold.maxValue !== templateObj.maxValue ||
          threshold.minDensityValue !== templateObj.minDensityValue ||
          threshold.maxDensityValue !== templateObj.maxDensityValue
        ) {
          isDefault = false

          break
        }
      }
    }

    setIsDefaultThresholds(isDefault)
  }, [riskThresholds, enabledCount])

  // Define the desired order using the RiskThresholdLevelCode enum
  const thresholdOrder: RiskThresholdLevelCode[] = [
    RiskThresholdLevelCode.LowRisk,
    RiskThresholdLevelCode.MediumLowRisk,
    RiskThresholdLevelCode.MediumRisk,
    RiskThresholdLevelCode.MediumHighRisk,
    RiskThresholdLevelCode.HighRisk
  ]

  // Sort the riskThresholds by code order
  const sortedThresholds = (riskThresholds || []).slice().sort((a, b) => {
    const aIdx = thresholdOrder.indexOf(a.code)
    const bIdx = thresholdOrder.indexOf(b.code)

    return aIdx - bIdx
  })

  return (
    <div className='min-h-[420px] flex flex-col tracking-tight'>
      <div className='flex justify-between'>
        <div className='mb-5 font-bold text-blue-navy uppercase'>
          Risk Threshold
        </div>
        <div className='font-bold'>{riskUnitDisplay}</div>
      </div>

      <div>
        <div className='grid grid-cols-[8rem_1fr_1fr] gap-x-4 items-center font-semibold mb-5'>
          <div>Risk Level</div>
          <div>From</div>
          <div>To</div>
        </div>

        {sortedThresholds.map((thresholdRow, thresholdIdx) => (
          <EditThresholdRow
            key={thresholdRow.code}
            isRiskDensity={isRiskDensity}
            thresholdRow={thresholdRow}
            onThresholdRowChange={(updatedThresholdRow) => {
              const newRiskThresholds = [...sortedThresholds]
              newRiskThresholds[thresholdIdx] = updatedThresholdRow
              onRiskThresholdsChange(newRiskThresholds)
            }}
          />
        ))}
      </div>

      <div className='mt-auto mb-4 h-10 flex gap-2 items-center'>
        {/* Add/Remove Range Buttons */}
        <Button
          kind={enabledCount === 5 ? 'primary' : 'secondary'}
          disabled={enabledCount === 5}
          onClick={() => {
            if (enabledCount < 5) {
              const nextCount = enabledCount + 1
              onRiskThresholdsChange([...riskThresholdsTemplate[nextCount]])
            }
          }}
        >
          + Add Range
        </Button>
        <Button
          kind={enabledCount === 3 ? 'primary' : 'ghost'}
          disabled={enabledCount === 3}
          onClick={() => {
            if (enabledCount > 3) {
              const nextCount = enabledCount - 1
              onRiskThresholdsChange([...riskThresholdsTemplate[nextCount]])
            }
          }}
        >
          Remove Last Range
        </Button>
        {/* Reset Fields Button */}
        {!isDefaultThresholds && (
          <Button kind='ghost' onClick={resetFields}>
            Reset Fields
          </Button>
        )}
      </div>
    </div>
  )
}
